import React from 'react';
import PropTypes from 'prop-types';
import StatusPill from '../../../../shared/components/pill/StatusPill';
import LabelPill from '../../../../shared/components/pill/LabelPill';

// A file can be in tag scope and still contribute nothing, which is the usual explanation for a
// confident "I don't know" from the assistant. So reachability and usability are shown as
// separate facts rather than collapsed into one "available" flag.
function usability(file) {
  if (!file.visible_to_field_engineers) {
    return { label: 'not shared', tone: 'neutral', title: 'Not shared with field engineers — excluded from retrieval' };
  }
  if (file.knowledge_index_status === 'ready') {
    return { label: `${file.fragment_count ?? 0} fragments`, tone: 'success', title: 'Indexed and retrievable' };
  }
  if (file.knowledge_index_status === 'failed') {
    return { label: 'index failed', tone: 'danger', title: 'Indexing failed — excluded from retrieval' };
  }
  return { label: file.knowledge_index_status || 'not indexed', tone: 'warning', title: 'Not yet indexed — excluded from retrieval' };
}

export default function FilesInScopeList({ files, loading, assetSelected }) {
  if (!assetSelected) {
    return <p className="text-muted small mb-0">Select an asset to see which files it can reach.</p>;
  }
  if (loading) {
    return <p className="text-muted small mb-0">Loading files…</p>;
  }
  if (files.length === 0) {
    return (
      <div className="alert alert-warning small py-2 mb-0" role="status">
        <i className="bi bi-exclamation-triangle me-1" />
        No files are in scope for this asset. The assistant has nothing to answer from.
      </div>
    );
  }

  const retrievable = files.filter(
    (f) => f.visible_to_field_engineers && f.knowledge_index_status === 'ready'
  ).length;

  return (
    <>
      <p className="text-muted small mb-2">
        {files.length} in tag scope · <strong>{retrievable}</strong> actually retrievable
      </p>
      <div className="d-flex flex-column gap-1">
        {files.map((file) => {
          const state = usability(file);
          return (
            <div key={file.media_id} className="border rounded p-2 bg-white">
              <div className="small fw-semibold text-truncate" title={file.filename}>
                {file.filename}
              </div>
              <div className="d-flex align-items-center gap-1 mt-1 flex-wrap">
                {(file.matched_tags || []).map((tag) => (
                  <LabelPill
                    key={tag}
                    tone="accent"
                    size="inline"
                    icon="bi-tag"
                    title="Tag that put this file in scope"
                  >
                    {tag}
                  </LabelPill>
                ))}
                <StatusPill tone={state.tone} size="inline" title={state.title}>{state.label}</StatusPill>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

FilesInScopeList.propTypes = {
  files: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  assetSelected: PropTypes.bool,
};

FilesInScopeList.defaultProps = { files: [], loading: false, assetSelected: false };
